
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

interface SessionAnalytic {
  id: string;
  event_type: string;
  created_at: string;
  session_start?: string;
  session_duration?: number;
  total_time?: number;
  user_agent?: string;
  viewport_width?: number;
  viewport_height?: number;
}

interface UserAnalytic {
  id: string;
  action_type: string;
  created_at: string;
  metadata?: any;
}

export const AnalyticsDebugger = () => {
  const [sessionAnalytics, setSessionAnalytics] = useState<SessionAnalytic[]>([]);
  const [userAnalytics, setUserAnalytics] = useState<UserAnalytic[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // Fetch session analytics
      const { data: sessionData, error: sessionError } = await supabase
        .from('session_analytics')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (sessionError) {
        console.error('Session analytics error:', sessionError);
        toast({
          title: "Error fetching session analytics",
          description: sessionError.message,
          variant: "destructive",
        });
      } else {
        setSessionAnalytics(sessionData || []);
      }

      // User analytics are now handled via Edge Function and not directly queryable
      // Show a note about the new analytics system instead
      setUserAnalytics([{
        id: 'note-1',
        action_type: 'Analytics Migration Notice',
        created_at: new Date().toISOString(),
        metadata: { 
          note: 'User analytics are now processed via Edge Function for better performance and security. Direct querying is disabled.'
        }
      }]);

    } catch (error) {
      console.error('Analytics fetch error:', error);
      toast({
        title: "Error",
        description: "Failed to fetch analytics data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Analytics Debugger</h2>
        <Button onClick={fetchAnalytics} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Session Analytics ({sessionAnalytics.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {sessionAnalytics.length === 0 ? (
                <p className="text-gray-500">No session analytics found</p>
              ) : (
                sessionAnalytics.map((session) => (
                  <div key={session.id} className="border-b pb-2">
                    <div className="font-semibold">{session.event_type}</div>
                    <div className="text-sm text-gray-600">
                      {formatDate(session.created_at)}
                    </div>
                    {session.session_duration && (
                      <div className="text-xs text-gray-500">
                        Duration: {session.session_duration}s
                      </div>
                    )}
                    {session.total_time && (
                      <div className="text-xs text-gray-500">
                        Total: {session.total_time}s
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>User Analytics ({userAnalytics.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {userAnalytics.length === 0 ? (
                <p className="text-gray-500">No user analytics found</p>
              ) : (
                userAnalytics.map((analytic) => (
                  <div key={analytic.id} className="border-b pb-2">
                    <div className="font-semibold">{analytic.action_type}</div>
                    <div className="text-sm text-gray-600">
                      {formatDate(analytic.created_at)}
                    </div>
                    {analytic.metadata && (
                      <div className="text-xs text-gray-500 mt-1">
                        <pre className="whitespace-pre-wrap break-all">
                          {JSON.stringify(analytic.metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Debug Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div>Current Time: {new Date().toLocaleString()}</div>
            <div>User Agent: {navigator.userAgent}</div>
            <div>Viewport: {window.innerWidth} x {window.innerHeight}</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
